import { toDash } from '../utils/string';
import SwupPreloadPlugin from '@swup/preload-plugin';
import SwupFragmentPlugin from '@swup/fragment-plugin';
import SwupScrollPlugin from '@swup/scroll-plugin';
// import SwupA11yPlugin from '@swup/a11y-plugin';
import Swup from 'swup';
import { Scroll } from './Scroll';
import { CSS_CLASS, CUSTOM_EVENT, SWUP_FRAGMENT } from '../config';
import { $html } from '../utils/dom';

export class Load {
  constructor() {
    this.onVisitStartBind = this.onVisitStart.bind(this);
    this.beforeContentReplaceBind = this.beforeContentReplace.bind(this);
    this.onContentReplaceBind = this.onContentReplace.bind(this);
    this.onAnimationInEndBind = this.onAnimationInEnd.bind(this);
    this.onAnimationOutStartBind = this.onAnimationOutStart.bind(this);
    this.onVisitEndBind = this.onVisitEnd.bind(this);
    this.onDocumentClickCaptureBind = this.onDocumentClickCapture.bind(this);
    this.onDocumentKeydownCaptureBind = this.onDocumentKeydownCapture.bind(this);
    this.onPublicationModalCloseBind = this.onPublicationModalClose.bind(this);
    this.goTo = this.goTo.bind(this);

    if (history.scrollRestoration) {
      history.scrollRestoration = 'manual';

      window.scrollTo(0, 0);
    }
  }

  // =============================================================================
  // Lifecycle
  // =============================================================================
  init() {
    this.initSwup();
    this.bindEvents();
    this.updateActiveLinks();

    requestAnimationFrame(() => {
      $html.classList.add(CSS_CLASS.READY);
      this.openPublicationModalIfDirectVisit();
    });
  }

  destroy() {
    this.unbindEvents();

    this.swup?.destroy();
  }

  // =============================================================================
  // Events
  // =============================================================================
  bindEvents() {
    window.addEventListener(CUSTOM_EVENT.GO_TO, this.goTo);
    window.addEventListener(CUSTOM_EVENT.PUBLICATION_MODAL_CLOSE, this.onPublicationModalCloseBind);
    // Capture-фаза, чтобы перехватить клик раньше Lenis/других обработчиков якорей
    document.addEventListener('click', this.onDocumentClickCaptureBind, true);
    document.addEventListener('keydown', this.onDocumentKeydownCaptureBind, true);

    this.swup.hooks.on('visit:start', this.onVisitStartBind);
    this.swup.hooks.before('content:replace', this.beforeContentReplaceBind);
    this.swup.hooks.on('content:replace', this.onContentReplaceBind);
    this.swup.hooks.on('animation:in:end', this.onAnimationInEndBind);
    this.swup.hooks.on('animation:out:start', this.onAnimationOutStartBind);
    this.swup.hooks.on('visit:end', this.onVisitEndBind);

    this.swup.hooks.on('fetch:error', e => {
      console.log('fetch:error:', e);
      debugger;
    });
    this.swup.hooks.on('fetch:timeout', e => {
      console.log('fetch:timeout:', e);
      debugger;
    });
  }

  unbindEvents() {
    window.removeEventListener(CUSTOM_EVENT.GO_TO, this.goTo);
    window.removeEventListener(
      CUSTOM_EVENT.PUBLICATION_MODAL_CLOSE,
      this.onPublicationModalCloseBind
    );
    document.removeEventListener('click', this.onDocumentClickCaptureBind, true);
    document.removeEventListener('keydown', this.onDocumentKeydownCaptureBind, true);
  }

  // =============================================================================
  // Methods
  // =============================================================================
  initSwup() {
    this.swup = new Swup({
      animateHistoryBrowsing: true,
      plugins: this._getSwupPlugins()
    });
  }

  /**
   * Retrieve HTML dataset on next container and update our real html element dataset accordingly
   *
   * @param visit
   */
  updateDocumentAttributes(visit) {
    const parser = new DOMParser();
    const nextDOM = parser.parseFromString(visit.to.html, 'text/html');
    const newDataset = {
      ...nextDOM.querySelector('html')?.dataset
    };

    Object.entries(newDataset).forEach(([key, val]) => {
      $html.setAttribute(`data-${toDash(key)}`, val ?? '');
    });
  }

  /**
   * Добавляет класс `.is-active` для <a>, который ведёт на текущую страницу.
   * Трогает ТОЛЬКО ссылки (<a>), чтобы не конфликтовать с `.is-active` у компонентов.
   */
  updateActiveLinks() {
    const normalizePath = (pathname = '/') => {
      let path = pathname;

      try {
        path = decodeURI(path);
      } catch {}

      path = path.replace(/\/index\.html?$/i, '/');

      if (path.length > 1) {
        path = path.replace(/\/+$/g, '');
      }

      return path || '/';
    };

    const { origin, pathname } = window.location;
    const currentPath = normalizePath(pathname);

    document.querySelectorAll('a[href]').forEach(link => {
      link.classList.remove('is-active');
      link.closest('[data-link-parent]')?.classList.remove('is-active');

      const { href } = link;

      // игнорируем якоря, mailto, tel
      if (
        !href ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        link.getAttribute('href')?.startsWith('#')
      ) {
        return;
      }

      let url;
      try {
        url = new URL(href);
      } catch {
        return;
      }

      if (url.origin !== origin) return;

      const linkPath = normalizePath(url.pathname);

      if (linkPath === currentPath) {
        link.classList.add('is-active');
        link.closest('[data-link-parent]')?.classList.add('is-active');
      }
    });
  }

  _getSwupPlugins() {
    const publicationFragmentRules = [
      {
        from: '(.*)',
        to: '/publications/:slug',
        containers: ['#publication-modal'],
        name: SWUP_FRAGMENT.PUBLICATION_MODAL
      },
      {
        from: '/publications/:slug',
        to: '(.*)',
        containers: ['#publication-modal'],
        name: SWUP_FRAGMENT.PUBLICATION_MODAL,
        if: visit => !this.isPublicationDetailUrl(visit.to.url)
      }
    ];

    const plugins = [
      new SwupScrollPlugin({
        // Передаём управление прокруткой Lenis/Locomotive через наш Scroll.
        // @see https://swup.js.org/plugins/scroll-plugin/
        doScrollingRightAway: true,
        // Если в URL нет hash, прыгаем в top сразу (без анимации).
        // Для якорей оставляем плавный скролл.
        animateScroll: {
          betweenPages: false,
          samePageWithHash: true,
          samePage: false
        },
        scrollFunction: (scrollContainer, top, left, animate, start, end) => {
          start?.();

          const immediate = !animate;

          if (!Scroll?.scrollTo) {
            window.scrollTo({ top, left, behavior: immediate ? 'auto' : 'smooth' });
            end?.();
            return;
          }

          try {
            Scroll.scrollTo(top, {
              immediate,
              force: true,
              onComplete: () => end?.()
            });
          } catch {
            window.scrollTo({ top, left, behavior: immediate ? 'auto' : 'smooth' });
            end?.();
          }
        }
      }),
      new SwupPreloadPlugin({
        preloadHoveredLinks: true
      }),
      new SwupFragmentPlugin({
        rules: publicationFragmentRules
      })
    ];

    return plugins;
  }

  isPublicationDetailUrl(url = window.location.href) {
    try {
      const { pathname } = new URL(url, window.location.origin);
      const path = pathname.replace(/\/+$/g, '') || '/';

      return /^\/publications\/[^/]+$/.test(path);
    } catch {
      return false;
    }
  }

  isPublicationFragmentVisit(visit) {
    return visit?.fragmentVisit?.name === SWUP_FRAGMENT.PUBLICATION_MODAL;
  }

  getPublicationModalContainer() {
    return document.getElementById('publication-modal');
  }

  getPublicationModal() {
    const container = this.getPublicationModalContainer();

    if (!container) return null;
    if (typeof container.open === 'function') return container;

    return container.querySelector('c-modal') || container;
  }

  openPublicationModal() {
    this.stopPageScroll();

    const container = this.getPublicationModalContainer();
    const modal = this.getPublicationModal();

    container?.classList?.add('is-active');
    modal?.open?.();
    modal?.rebindTogglers?.();
  }

  stopPageScroll() {
    Scroll?.stop?.();
  }

  resumePageScroll() {
    if (!Scroll.locomotiveScroll) {
      Scroll.init();
      return;
    }

    Scroll.start();
  }

  isPublicationModalClosingVisit(visit) {
    return (
      this.isPublicationFragmentVisit(visit) && !this.isPublicationDetailUrl(visit.to.url)
    );
  }

  openPublicationModalIfDirectVisit() {
    if (!this.isPublicationDetailUrl()) return;

    this.openPublicationModal();
  }

  isPublicationModalOpen() {
    const container = this.getPublicationModalContainer();

    if (!container || container.tagName === 'TEMPLATE') return false;

    const modal = this.getPublicationModal();

    return (
      modal?.isOpen === true ||
      modal?.classList?.contains('is-active') ||
      $html.classList.contains('has-publication-open') ||
      this.isPublicationDetailUrl()
    );
  }

  getPublicationModalCloseUrl() {
    const swupEl = document.getElementById('swup');
    const fragmentUrl = swupEl?.getAttribute('data-swup-fragment-url');

    if (fragmentUrl) return fragmentUrl;

    const closeLink = document.querySelector(
      '#publication-modal [data-swup-link-to-fragment], #publication-modal a[href][data-publication-toggler]'
    );

    if (!closeLink?.href) return null;

    try {
      const url = new URL(closeLink.href, window.location.href);

      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }

  closePublicationModal() {
    if (!this.isPublicationModalOpen()) return;

    if (this.isPublicationDetailUrl() && window.history.length > 1) {
      window.history.back();
      return;
    }

    const closeUrl = this.getPublicationModalCloseUrl();

    if (closeUrl && !this.swup?.isSameResolvedUrl?.(closeUrl, window.location.href)) {
      this.swup?.navigate?.(closeUrl);
      return;
    }

    window.history.back();
  }

  onPublicationModalClose() {
    this.closePublicationModal();
  }

  // =============================================================================
  // Hooks
  // =============================================================================

  /**
   * On `transition.goTo` custom event
   * Navigate to a new page
   *
   * @param e
   */
  goTo(e) {
    this.swup.navigate(e.detail);
  }

  onDocumentKeydownCapture(e) {
    if (e.key !== 'Escape' && e.key !== 'Esc' && e.keyCode !== 27) return;
    if (!this.isPublicationModalOpen()) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation?.();

    this.closePublicationModal();
  }

  onDocumentClickCapture(e) {
    const publicationClose = e.target?.closest?.('#publication-modal [data-publication-toggler]');

    if (publicationClose && this.isPublicationModalOpen()) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation?.();
      this.closePublicationModal();
      return;
    }

    // Только обычный левый клик без модификаторов
    if (e.defaultPrevented) return;
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    const link = e.target?.closest?.('a[href]');
    if (!link) return;
    if (link.target === '_blank') return;
    if (link.hasAttribute('download')) return;
    if (link.getAttribute('data-no-swup') != null) return;

    const hrefAttr = link.getAttribute('href');
    if (!hrefAttr) return;
    if (hrefAttr.startsWith('mailto:') || hrefAttr.startsWith('tel:')) return;

    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch {
      return;
    }

    if (url.origin !== window.location.origin) return;
    if (!url.hash || url.hash === '#') return;

    const normalizePath = (pathname = '/') => {
      let path = pathname;
      try {
        path = decodeURI(path);
      } catch {}
      path = path.replace(/\/index\.html?$/i, '/');
      if (path.length > 1) path = path.replace(/\/+$/g, '');
      return path || '/';
    };

    const currentPath = normalizePath(window.location.pathname);
    const targetPath = normalizePath(url.pathname);

    // Если это переход на ДРУГУЮ страницу с hash (/about#meet),
    // перехватываем, чтобы Lenis не пытался скроллить по hash на старой странице.
    if (targetPath !== currentPath) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation?.();

      this.swup?.navigate?.(`${url.pathname}${url.search}${url.hash}`);
    }
  }

  /**
   * On visit:start
   * Transition to a new page begins
   *
   * @see https://swup.js.org/hooks/#visit-start
   * @param visit
   */
  onVisitStart(visit) {
    if (this.isPublicationFragmentVisit(visit)) {
      if (this.isPublicationDetailUrl(visit.to.url)) {
        this.stopPageScroll();
      }
    } else {
      // Пусть Swup сбрасывает scroll (мы дополнительно форсим reset сразу после content:replace)
      visit.scroll.reset = true;
      $html.classList.add(CSS_CLASS.TRANSITION);
      $html.classList.remove(CSS_CLASS.READY);
    }

    // Dispatch custom event
    window.dispatchEvent(
      new CustomEvent(CUSTOM_EVENT.VISIT_START, {
        detail: visit
      })
    );
  }

  /**
   * On before:content:replace
   * The old content of the page is replaced by the new content.
   *
   * @see https://swup.js.org/hooks/#content-replace
   * @param visit
   */
  beforeContentReplace(visit) {
    if (!visit.fragmentVisit) {
      Scroll?.destroy();
    }
  }

  /**
   * Сброс и реинициализация Webflow после замены контента (переход по страницам).
   * IX2: destroy + init. Старый API: destroy + ready (если есть).
   */
  resetWebflow() {
    const w = window.Webflow;
    if (!w) return;

    try {
      // Полный сброс всех модулей (включая формы), затем повторная инициализация.
      w.destroy?.();
      w.ready?.();

      console.log('webflow ready');
      // Дополнительно: если есть IX2, убедимся, что он снова инициализирован.
      if (w.require?.('ix2')) {
        const ix2 = w.require('ix2');
        ix2.destroy?.();
        ix2.init?.();
      }

      // Явная переинициализация модуля форм, если он доступен.
    } catch (_) {}
  }

  /**
   * On content:replace
   * The old content of the page is replaced by the new content.
   *
   * @see https://swup.js.org/hooks/#content-replace
   * @param visit
   */
  onContentReplace(visit) {
    // const adminBar = document.getElementById('wpadminbar');
    // if (adminBar) {
    //   adminBar.remove();
    //   var parser = new DOMParser();
    //   var doc = parser.parseFromString(visit.to.html, 'text/html');
    //   var newAdminBar = doc.getElementById('wpadminbar');
    //   newAdminBar && document.body.appendChild(newAdminBar);
    // }

    if (this.isPublicationFragmentVisit(visit)) {
      requestAnimationFrame(() => this.resetWebflow());
      return;
    }

    if (!visit.fragmentVisit) {
      Scroll?.init();
      this.updateDocumentAttributes(visit);
      this.updateActiveLinks();
      requestAnimationFrame(() => this.resetWebflow());
    }
  }

  onVisitEnd(visit) {
    if (this.isPublicationModalClosingVisit(visit)) {
      this.getPublicationModalContainer()?.classList?.remove('is-active');
      this.resumePageScroll();
    }
  }

  /**
   * On animation:out:start
   * Current content starts animating out. Class `.is-animating` is added.
   *
   * @see https://swup.js.org/hooks/#animation-out-start
   * @param visit
   */
  onAnimationOutStart(visit) {
    if (!visit.fragmentVisit) {
      const scrollY = window.scrollY;
      $html.style.setProperty('--scroll-y', `${scrollY}px`);

      const transformOrigin = scrollY + window.innerHeight / 2;
      $html.style.setProperty('--transform-origin', `50% ${transformOrigin}px`);
      $html.classList.remove('has-menu-open');
      $html.classList.remove('has-modal-open');
    }
  }

  /**
   * On animation:in:end
   * New content finishes animating out.
   *
   * @see https://swup.js.org/hooks/#animation-in-end
   * @param visit
   */
  onAnimationInEnd(visit) {
    if (this.isPublicationFragmentVisit(visit)) {
      if (this.isPublicationDetailUrl(visit.to.url)) {
        this.openPublicationModal();
      } else {
        this.getPublicationModalContainer()?.classList?.remove('is-active');
        this.resumePageScroll();
      }

      return;
    }

    if (!visit.fragmentVisit) {
      $html.classList.remove(CSS_CLASS.TRANSITION);
      $html.classList.add(CSS_CLASS.READY);
      $html.classList.remove('has-footer');
    }
  }
}
